import { useContext, FC, useState, useEffect, useMemo } from 'react'
import { Center } from '@chakra-ui/react'

import { Loader } from 'components'
import CustomFormBuilderContext, { CustomFormBuilderProps } from './Context'
import { CustomForm, CustomFormQuestion } from '../../../../types'

export type CustomFormPermissions = {
  updateForm?: boolean
  updateFormVisibility?: boolean
  deleteForm?: boolean
  createFormQuestion?: boolean
  updateFormQuestion?: boolean
  deleteFormQuestion?: boolean
}

export type Props = {
  permissions?: CustomFormPermissions
  children?: JSX.Element
  _updateForm?: any
  _updateFormVisibility?: any
  _deleteForm?: any
  _getForm: any
  _getQuestion: any
  _readQuestions: any
  _createQuestion: any
  _updateQuestion: any
  _deleteQuestion: any
}

const CustomFormBuilderProvider: FC<Props> = ({
  children,
  permissions,
  _updateForm,
  _updateFormVisibility,
  _deleteForm,
  _getForm,
  _getQuestion,
  _readQuestions,
  _createQuestion,
  _updateQuestion,
  _deleteQuestion,
}) => {
  const [loadingForm, setLoadingForm] = useState(true)
  const [form, setForm] = useState<CustomForm | undefined>()
  const [questions, setQuestions] = useState<CustomFormQuestion[]>([])

  const canUpdateForm = !!_updateForm
  const canDeleteForm = !!_deleteForm
  const canUpdateFormVisibility = !!_updateFormVisibility

  const isFormActionsDisabled = useMemo(() => {
    return form?.is_protected || form?.is_restricted || false
  }, [form])

  const updateFormVisibility = async (data: any) => {
    if (!_updateFormVisibility) return {}
    const response = await _updateFormVisibility({ data, formId: form?.id })
    return response
  }

  const updateForm = async (data: any) => {
    if (!_updateForm) return {}
    const response = await _updateForm({ data, formId: form?.id })
    return response
  }

  const deleteForm = async () => {
    if (!_deleteForm) return false
    const response = await _deleteForm()
    if (response?.header?.success) {
      await readQuestions()
    }
    setLoadingForm(false)
  }

  const getQuestion = async (questionSlug?: string) => {
    const response = await _getQuestion({ questionSlug })
    return response
  }

  const deleteQuestion = async (questionId?: string) => {
    const response = await _deleteQuestion({ questionId })
    if (response?.header?.success) {
      await readQuestions()
    }
    return true
  }

  const updateQuestion = async ({ data, questionId }: any) => {
    const response = await _updateQuestion({ questionId, data })
    if (response?.header?.success) {
      await readQuestions()
    }
    return true
  }

  const createQuestion = async ({ data }: any) => {
    const response = await _createQuestion({ data })
    if (response?.header?.success) {
      const surveyFormQuestion = response?.body?.form_question
      setQuestions([...questions, surveyFormQuestion])
    }
    return true
  }

  const readQuestions = async () => {
    const response = await _readQuestions()
    if (response?.header?.success) {
      const responseValues = response?.body?.form_question?.elements
      setQuestions(responseValues)
    }
  }

  const getForm = async () => {
    const response = await _getForm()
    if (response?.header?.success) {
      const responseValues = response?.body?.form
      setForm(responseValues)
      await readQuestions()
    }
    setLoadingForm(false)
  }

  useEffect(() => {
    getForm()
  }, [])

  return (
    <CustomFormBuilderContext.Provider
      value={{
        isFormActionsDisabled,
        canUpdateFormVisibility,
        canUpdateForm,
        canDeleteForm,
        permissions,
        form,
        setForm,
        deleteForm,
        updateForm,
        updateFormVisibility,
        getQuestion,
        readQuestions,
        createQuestion,
        updateQuestion,
        deleteQuestion,
        questions,
        setQuestions,
      }}
    >
      {loadingForm ? (
        <Center h="300px">
          <Loader text="Carregando formulário..." />
        </Center>
      ) : (
        children
      )}
    </CustomFormBuilderContext.Provider>
  )
}

export default CustomFormBuilderProvider

export const useCustomFormBuilder = () => useContext<CustomFormBuilderProps>(CustomFormBuilderContext)
